// src/components/EditorUI.tsx

import React, { useState, useCallback } from 'react';
import { useCityStore } from '../store/useCityStore';
import styles from './EditorUI.module.css';

// ----------------------------------------------------------------------
// Tipos de props
// ----------------------------------------------------------------------

export interface EditorUIProps {
  /** Si el panel de edición está visible */
  visible?: boolean;
  /** Callback para cambiar entre modo editor y modo juego */
  onTogglePlayMode?: (isPlaying: boolean) => void;
  /** Callback para exportar a GLTF */
  onExport?: () => void;
  /** Callback para guardar proyecto */
  onSave?: (name: string) => void;
  /** Callback para cargar proyecto */
  onLoad?: (name: string) => void;
}

// ----------------------------------------------------------------------
// Componente principal
// ----------------------------------------------------------------------

const EditorUI: React.FC<EditorUIProps> = ({
  visible = true,
  onTogglePlayMode,
  onExport,
  onSave,
  onLoad,
}) => {
  const {
    buildings,
    roads,
    selectedObject,
    selectObject,
    updateBuildingProperty,
    removeBuilding,
  } = useCityStore();

  const [isPlaying, setIsPlaying] = useState(false);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [showLoadModal, setShowLoadModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [projectName, setProjectName] = useState('mi_ciudad');
  const [savedProjects, setSavedProjects] = useState<string[]>(() => {
    const keys = Object.keys(localStorage).filter((k) => k.startsWith('city_'));
    return keys.map((k) => k.replace('city_', ''));
  });

  // Confirmar y eliminar edificio seleccionado
  const handleConfirmDelete = useCallback(() => {
    if (selectedObject) {
      removeBuilding(selectedObject.id);
      selectObject(null);
    }
    setShowDeleteConfirm(false);
  }, [selectedObject, removeBuilding, selectObject]);

  // Manejar cambio de modo
  const handleTogglePlay = useCallback(() => {
    const newPlaying = !isPlaying;
    setIsPlaying(newPlaying);
    if (onTogglePlayMode) {
      onTogglePlayMode(newPlaying);
    }
  }, [isPlaying, onTogglePlayMode]);

  // Guardar proyecto
  const handleSave = useCallback(() => {
    if (onSave) {
      onSave(projectName);
    } else {
      // Guardado directo en localStorage
      const data = {
        buildings,
        roads,
        savedAt: new Date().toISOString(),
      };
      localStorage.setItem(`city_${projectName}`, JSON.stringify(data));
      setSavedProjects((prev) => [...new Set([...prev, projectName])]);
    }
    setShowSaveModal(false);
  }, [projectName, buildings, roads, onSave]);

  // Cargar proyecto
  const handleLoad = useCallback(
    (name: string) => {
      if (onLoad) {
        onLoad(name);
      } else {
        const raw = localStorage.getItem(`city_${name}`);
        if (raw) {
          const data = JSON.parse(raw);
          // Restaurar los datos cargados en el store
          useCityStore.getState().setBuildings(data.buildings ?? []);
          useCityStore.getState().setRoads(data.roads ?? []);
        }
      }
      setShowLoadModal(false);
    },
    [onLoad]
  );

  // Editar propiedad de edificio seleccionado
  const handleHeightChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (selectedObject && 'height' in selectedObject) {
        const newHeight = parseFloat(e.target.value) || 0;
        updateBuildingProperty(selectedObject.id, 'height', newHeight);
      }
    },
    [selectedObject, updateBuildingProperty]
  );

  const handleColorChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (selectedObject && 'tags' in selectedObject) {
        updateBuildingProperty(selectedObject.id, 'color', e.target.value);
      }
    },
    [selectedObject, updateBuildingProperty]
  );

  // Si no está visible, no renderizar
  if (!visible) return null;

  return (
    <div className={styles.container}>
      {/* Barra superior */}
      <div className={styles.topBar}>
        <span className={styles.brandTitle}>
          🏙️ City Editor 3D
        </span>

        <button
          onClick={handleTogglePlay}
          className={isPlaying ? styles.btnPlayActive : styles.btnPlay}
        >
          {isPlaying ? '⏹ Detener Juego' : '▶️ Modo Juego'}
        </button>

        <button
          onClick={() => setShowSaveModal(true)}
          className={styles.btnSave}
        >
          💾 Guardar
        </button>

        <button
          onClick={() => setShowLoadModal(true)}
          className={styles.btnLoad}
        >
          📂 Cargar
        </button>

        {onExport && (
          <button
            onClick={onExport}
            className={styles.btnExport}
          >
            📤 Exportar GLTF
          </button>
        )}
      </div>

      {/* Panel lateral izquierdo: lista de edificios */}
      <div className={styles.leftPanel}>
        <h3 className={styles.panelTitle}>
          Edificios ({buildings.length})
        </h3>
        {buildings.length === 0 ? (
          <p className={styles.emptyText}>No hay edificios cargados</p>
        ) : (
          <ul className={styles.itemList}>
            {buildings.map((b) => (
              <li
                key={b.id}
                onClick={() => selectObject(b)}
                className={
                  selectedObject?.id === b.id
                    ? styles.buildingItemSelected
                    : styles.buildingItem
                }
              >
                <span>{b.name || `Edificio ${b.id}`}</span>
                <span className={styles.buildingHeightBadge}>
                  {b.height.toFixed(0)}m
                </span>
              </li>
            ))}
          </ul>
        )}

        <h3 className={styles.panelTitleSpacing}>
          Calles ({roads.length})
        </h3>
        {roads.length === 0 ? (
          <p className={styles.emptyText}>No hay calles cargadas</p>
        ) : (
          <ul className={styles.itemList}>
            {roads.slice(0, 10).map((r) => (
              <li key={r.id} className={styles.roadItem}>
                {r.name || `Calle ${r.id}`} ({r.highwayType})
              </li>
            ))}
            {roads.length > 10 && (
              <li className={styles.moreItemsText}>
                ... y {roads.length - 10} más
              </li>
            )}
          </ul>
        )}
      </div>

      {/* Panel lateral derecho: propiedades del seleccionado */}
      <div className={styles.rightPanel}>
        <h3 className={styles.panelTitle}>
          Propiedades
        </h3>
        {selectedObject ? (
          <div>
            <div className={styles.formGroup}>
              <label className={styles.fieldLabel}>
                Nombre
              </label>
              <input
                type="text"
                value={selectedObject.name || ''}
                onChange={(e) =>
                  updateBuildingProperty(selectedObject.id, 'name', e.target.value)
                }
                className={styles.textInput}
              />
            </div>

            <div className={styles.formGroup}>
              <label className={styles.fieldLabel}>
                Altura (metros)
              </label>
              <input
                type="number"
                value={selectedObject.height || 0}
                onChange={handleHeightChange}
                min={1}
                max={500}
                className={styles.textInput}
              />
            </div>

            <div className={styles.formGroup}>
              <label className={styles.fieldLabel}>
                Color
              </label>
              <input
                type="color"
                value={selectedObject.color || '#cccccc'}
                onChange={handleColorChange}
                className={styles.colorInput}
              />
            </div>

            <button
              onClick={() => setShowDeleteConfirm(true)}
              className={styles.btnDelete}
            >
              🗑️ Eliminar edificio
            </button>
          </div>
        ) : (
          <p className={styles.emptyText}>
            Haz clic en un edificio para ver sus propiedades
          </p>
        )}
      </div>

      {/* Barra inferior */}
      <div className={styles.bottomBar}>
        <span>🖱️ Click: seleccionar</span>
        <span>|</span>
        <span>🔄 Rueda: zoom</span>
        <span>|</span>
        <span>✋ Arrastrar: rotar</span>
      </div>

      {/* Modal de guardar */}
      {showSaveModal && (
        <ModalOverlay onClose={() => setShowSaveModal(false)}>
          <h3 className={styles.modalTitle}>Guardar proyecto</h3>
          <input
            type="text"
            value={projectName}
            onChange={(e) => setProjectName(e.target.value)}
            placeholder="Nombre del proyecto"
            className={styles.modalInput}
          />
          <div className={styles.modalActions}>
            <button
              onClick={() => setShowSaveModal(false)}
              style={modalButtonStyle('#666')}
            >
              Cancelar
            </button>
            <button onClick={handleSave} style={modalButtonStyle('#4CAF50')}>
              Guardar
            </button>
          </div>
        </ModalOverlay>
      )}

      {/* Modal de cargar */}
      {showLoadModal && (
        <ModalOverlay onClose={() => setShowLoadModal(false)}>
          <h3 className={styles.modalTitle}>Cargar proyecto</h3>
          {savedProjects.length === 0 ? (
            <p className={styles.emptyText}>No hay proyectos guardados</p>
          ) : (
            <ul className={styles.itemList}>
              {savedProjects.map((name) => (
                <li
                  key={name}
                  onClick={() => handleLoad(name)}
                  className={styles.savedProjectItem}
                >
                  📁 {name}
                </li>
              ))}
            </ul>
          )}
          <div className={styles.modalActions}>
            <button
              onClick={() => setShowLoadModal(false)}
              style={modalButtonStyle('#666')}
            >
              Cerrar
            </button>
          </div>
        </ModalOverlay>
      )}

      {/* Modal de confirmación de eliminación */}
      {showDeleteConfirm && selectedObject && (
        <ModalOverlay onClose={() => setShowDeleteConfirm(false)}>
          <h3 className={styles.modalTitle}>Confirmar eliminación</h3>
          <p className={styles.modalDescription}>
            ¿Estás seguro de que deseas eliminar el edificio{' '}
            <strong>{selectedObject.name || `Edificio ${selectedObject.id}`}</strong>?
          </p>
          <div className={styles.modalActions}>
            <button
              onClick={() => setShowDeleteConfirm(false)}
              style={modalButtonStyle('#666')}
            >
              Cancelar
            </button>
            <button
              onClick={handleConfirmDelete}
              style={modalButtonStyle('#f44336')}
            >
              Eliminar
            </button>
          </div>
        </ModalOverlay>
      )}
    </div>
  );
};

// ----------------------------------------------------------------------
// Componente auxiliar ModalOverlay
// ----------------------------------------------------------------------

const ModalOverlay: React.FC<{ onClose: () => void; children: React.ReactNode }> = ({
  onClose,
  children,
}) => {
  return (
    <div className={styles.modalBackdrop} onClick={onClose}>
      <div className={styles.modalCard} onClick={(e) => e.stopPropagation()}>
        {children}
      </div>
    </div>
  );
};

// ----------------------------------------------------------------------
// Estilo reutilizable para botones de modal
// ----------------------------------------------------------------------

const modalButtonStyle = (bgColor: string): React.CSSProperties => ({
  padding: '8px 16px',
  background: bgColor,
  color: 'white',
  border: 'none',
  borderRadius: '4px',
  cursor: 'pointer',
  fontSize: '14px',
});

export default EditorUI;